import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useContext,
} from "react";

import MobiledataOffIcon from "@mui/icons-material/MobiledataOff";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  CircularProgress,
  Alert,
} from "@mui/material";
import type {
  Conversation,
  ConversationClient,
  ConversationTemplate,
} from "@rubi/api-generated/clientRs";
import {
  listConversationTemplate,
  patchConversation,
  postConversation,
  postConversationOutboundMessage,
  getConversationClient,
  getConversation,
} from "@rubi/api-generated/clientRs";
import { PageHeader } from "@rubi/ui";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { useConversationsInfiniteQuery } from "@/api/loadConversations";
import { Permission } from "@/components/UserManagement/types";
import { userPermissionChecks } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";

import ChatConversation from "./ChatComponents/ChatConversation";
import ChatMessageBox from "./ChatComponents/ChatMessageBox";
import ContactNameEditor from "./ChatComponents/ContactNameEditor";
import TemplateSelector from "./ChatComponents/TemplateSelector";
import LanguageMismatchDialog from "./LanguageMismatchDialog";
import PhoneInputCard from "./PhoneInputView";
import {
  appendLangParam,
  findRubiconReportUrls,
  normalizeLangForReferral,
  type RubiconReportUrlMatch,
} from "./utils/detectReportUrl";

const CONVERSATION_PAGE_SIZE = 10;

type PendingMessage = {
  message: { content: string; lang: string };
  candidateUrls: RubiconReportUrlMatch[];
};

// Helper function to extract error message from API errors
function extractErrorMessage(err: any, defaultMessage: string): string {
  // Try to extract ErrorResponse from various possible locations
  let errorResponse: any = null;

  // Check err.error first (@hey-api/client-axios format)
  if (err?.error) {
    errorResponse = err.error;
  }
  // Check err.response?.data (axios format)
  else if (err?.response?.data) {
    errorResponse = err.response.data;
  }

  // If we have an ErrorResponse, extract the message
  if (errorResponse) {
    // Check for console_msg (can be string or object)
    if (errorResponse.console_msg) {
      if (typeof errorResponse.console_msg === "string") {
        return errorResponse.console_msg;
      }
      // If it's an object, try to stringify it
      try {
        return JSON.stringify(errorResponse.console_msg);
      } catch {
        // If stringification fails, continue to other options
      }
    }
    // Check for msg field
    if (errorResponse.msg) {
      return errorResponse.msg;
    }
  }

  // Fallback to err.message or default
  return err?.message || defaultMessage;
}

const ChatView = () => {
  // phoneNumber is absent on /rubimessage/:orgId, where this view shows the
  // phone input card instead of a conversation. Templates can already be
  // pre-loaded into the message box there; entering a number keeps them because
  // both routes render this component, so it is never remounted.
  const { phoneNumber, orgId } = useParams<{
    phoneNumber?: string;
    orgId: string;
  }>();
  const navigate = useNavigate();
  const { t } = useTranslation("message");

  const { currentUser } = useContext(AuthStateContext);
  const { canSendFreeformMessage } = userPermissionChecks(
    currentUser?.permissions?.map((p) => p.title as Permission) ?? [],
  );

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [conversationClient, setConversationClient] =
    useState<ConversationClient | null>(null);
  const [currentConversation, setCurrentConversation] =
    useState<Conversation | null>(null);
  const [templates, setTemplates] = useState<ConversationTemplate[]>([]);
  const [autoReplyMessage, setAutoReplyMessage] = useState<string>("");
  const [oneWayNotificationMessage, setOneWayNotificationMessage] =
    useState<string>("");
  const [messageText, setMessageText] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [selectedTemplateContent, setSelectedTemplateContent] = useState<
    Array<{ lang: string; content: string }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const messagesAreaRef = useRef<HTMLDivElement>(null);
  const hasScrolledToBottomRef = useRef(false);
  const previousConversationCountRef = useRef(0);
  const scrollHeightBeforeFetchRef = useRef<number | null>(null);
  // Pending conversation data for when no active conversation exists
  const [pendingConversationData, setPendingConversationData] = useState<{
    first_name?: string;
    last_name?: string;
    auto_reply_enabled?: boolean;
  }>({});

  const [pendingMessages, setPendingMessages] = useState<
    PendingMessage[] | null
  >(null);

  const scrollToBottom = () => {
    const container = messagesAreaRef.current;
    if (!container) return;

    // Use requestAnimationFrame to ensure DOM is fully rendered
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
    });
  };

  const {
    data: conversationsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: conversationsLoading,
    isError: conversationsError,
    error: conversationsErrorObj,
  } = useConversationsInfiniteQuery({
    rubiOrgId: orgId,
    smsPhoneNumber: phoneNumber,
    itemsPerPage: CONVERSATION_PAGE_SIZE,
    includeParticipants: true,
    includeInboundMessages: true,
    includeOutboundMessages: true,
  });

  // Get the currently selected template object
  const selectedTemplateObj = selectedTemplate
    ? templates.find((t) => t.id === selectedTemplate)
    : null;

  // Helper functions
  const getActiveConversation = (conversations: Conversation[]) => {
    return conversations.find((c) => !c.time_closed);
  };

  // Drop conversation state when the phone number changes. This component stays
  // mounted across the /rubimessage/:orgId <-> .../conversation/:phoneNumber
  // transition, so nothing else clears it and the previous number's history
  // would leak into the next one. Runs as a layout effect so it lands before the
  // merge effect below, which may repopulate synchronously from cached data.
  useLayoutEffect(() => {
    setConversations([]);
    setCurrentConversation(null);
    hasScrolledToBottomRef.current = false;
    previousConversationCountRef.current = 0;
  }, [phoneNumber]);

  useEffect(() => {
    if (!conversationsData?.pages) return;

    const merged = conversationsData.pages.flatMap(
      (page) => page?.conversations ?? [],
    );

    // Merge with existing conversations to preserve manually added ones (e.g., newly created)
    setConversations((prev) => {
      const existingIds = new Set(merged.map((c) => c.id));
      // Keep conversations that exist in query results or are manually added (not yet in query)
      const manuallyAdded = prev.filter((c) => !existingIds.has(c.id));
      // Merge query results with manually added conversations, removing duplicates
      const allConversations = [...merged, ...manuallyAdded];
      // Remove duplicates based on id, keeping the most recent version
      const uniqueConversations = Array.from(
        new Map(allConversations.map((c) => [c.id, c])).values(),
      );
      return uniqueConversations;
    });
  }, [conversationsData]);

  // Update currentConversation whenever conversations change
  useEffect(() => {
    const updateCurrentConversation = () => {
      if (conversations.length > 0) {
        const activeConversation = getActiveConversation(conversations);

        if (activeConversation) {
          setCurrentConversation(activeConversation);
        } else {
          // No active conversation, set to null
          // We'll create one when the user sends a message
          setCurrentConversation(null);
        }
      } else {
        setCurrentConversation(null);
      }
    };

    updateCurrentConversation();
  }, [conversations]);

  useEffect(() => {
    if (!phoneNumber) return;

    if (conversations.length > 0) {
      const mostRecent = [...conversations].sort(
        (a, b) =>
          new Date(b.time_created).getTime() -
          new Date(a.time_created).getTime(),
      )[0];
      setPendingConversationData({
        first_name: mostRecent.first_name || undefined,
        last_name: mostRecent.last_name || undefined,
        auto_reply_enabled: mostRecent.auto_reply_enabled ?? true,
      });
    } else {
      setPendingConversationData({
        auto_reply_enabled: true,
      });
    }
  }, [conversations, phoneNumber]);

  useEffect(() => {
    if (conversationsError) {
      const errorMessage =
        (conversationsErrorObj as Error)?.message ||
        t("chat.errors.failedLoadConversations");
      setError(errorMessage);
    }
  }, [conversationsError, conversationsErrorObj]);

  // Load the client and its templates. Not conversation-scoped, so this runs
  // once per org and is not repeated when a phone number is entered.
  useEffect(() => {
    const loadData = async () => {
      if (!orgId) return;

      try {
        setLoading(true);
        setError(null);

        const [clientResponse, templatesResponse] = await Promise.all([
          getConversationClient({
            query: {
              rubi_org_id: orgId,
            },
          }),
          listConversationTemplate({
            query: {
              rubi_org_id: orgId,
            },
          }),
        ]);

        const client = clientResponse.data;
        const allTemplates =
          templatesResponse.data?.conversation_templates || [];
        if (client) {
          setConversationClient(client);

          // Find and set auto reply message
          const autoReplyTemplate = allTemplates.find(
            (template) => template.id === client.auto_reply_template_id,
          );

          if (autoReplyTemplate) {
            const englishContent = autoReplyTemplate.content?.find(
              (c) => c.lang === client.auto_reply_template_lang,
            );
            const content =
              englishContent?.content ||
              autoReplyTemplate.content?.[0]?.content ||
              "";

            setAutoReplyMessage(content);
          }

          // Find and set one-way notification message
          const oneWayTemplate = allTemplates.find(
            (template) =>
              template.id === client.one_way_notification_template_id,
          );
          if (oneWayTemplate) {
            const englishContent = oneWayTemplate.content?.find(
              (c) => c.lang === "en",
            );
            const content =
              englishContent?.content ||
              oneWayTemplate.content?.[0]?.content ||
              "";
            setOneWayNotificationMessage(content);
          }

          // Filter out auto_reply and one_way_notification templates
          const filteredTemplates = allTemplates.filter((template) => {
            return (
              template.id !== client.auto_reply_template_id &&
              template.id !== client.one_way_notification_template_id
            );
          });

          setTemplates(filteredTemplates);
        }
      } catch (err: any) {
        console.error("Error loading data:", err);
        const errorMessage = extractErrorMessage(
          err,
          t("chat.errors.failedLoadConversation"),
        );
        toast.error(
          t("chat.errors.couldNotLoadConversation", {
            message: errorMessage,
          }),
        );
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [orgId]);

  // Initial load: scroll to bottom synchronously before browser paints
  useLayoutEffect(() => {
    if (
      !conversationsLoading &&
      conversations.length > 0 &&
      !hasScrolledToBottomRef.current
    ) {
      hasScrolledToBottomRef.current = true;
      const container = messagesAreaRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [conversations, conversationsLoading]);

  // Subsequent updates: scroll if user is near bottom
  useEffect(() => {
    const currentCount = conversations.length;
    const previousCount = previousConversationCountRef.current;

    if (currentCount > previousCount && previousCount > 0) {
      const container = messagesAreaRef.current;
      if (container) {
        const isNearBottom =
          container.scrollHeight -
            container.scrollTop -
            container.clientHeight <
          100;

        if (isNearBottom) {
          scrollToBottom();
        }
      }
    }

    previousConversationCountRef.current = currentCount;
  }, [conversations]);

  // Poll the current (active) conversation for new messages
  useEffect(() => {
    if (!currentConversation || currentConversation.time_closed) return;

    const POLLING_INTERVAL = 1000 * 20; // 20 seconds
    const intervalId = setInterval(async () => {
      try {
        const response = await getConversation({
          path: { conversation_id: currentConversation.id },
        });
        if (response.data) {
          setConversations((prev) =>
            prev.map((c) => (c.id === response.data!.id ? response.data! : c)),
          );
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, POLLING_INTERVAL);

    return () => clearInterval(intervalId);
  }, [currentConversation?.id, currentConversation?.time_closed == null]);

  // Preserve scroll position when older conversations are prepended
  useLayoutEffect(() => {
    const container = messagesAreaRef.current;
    if (scrollHeightBeforeFetchRef.current != null && container) {
      const newScrollHeight = container.scrollHeight;
      container.scrollTop =
        newScrollHeight - scrollHeightBeforeFetchRef.current;
      scrollHeightBeforeFetchRef.current = null;
    }
  }, [conversations]);

  const handleMessagesScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const isAtTop = target.scrollTop <= 0;

    if (isAtTop && hasNextPage && !isFetchingNextPage) {
      scrollHeightBeforeFetchRef.current =
        messagesAreaRef.current?.scrollHeight ?? null;
      fetchNextPage();
    }
  };

  const formatPhoneNumber = (phone: string) => {
    const cleaned = phone.replace(/[^\d+]/g, "");

    // for now, we only support North American numbers.
    // to support international numbers, we should use
    // package like libphonenumber-js
    if (cleaned.startsWith("+1") && cleaned.length === 12) {
      const areaCode = cleaned.slice(2, 5);
      const prefix = cleaned.slice(5, 8);
      const lineNumber = cleaned.slice(8);
      return `+1 (${areaCode}) ${prefix}-${lineNumber}`;
    }

    return phone;
  };

  const handleTemplateSelect = (template: ConversationTemplate) => {
    // Toggle: if clicking the already-selected template, deselect it
    if (selectedTemplate === template.id) {
      handleClearTemplate();
      return;
    }

    // Get English content from template, or first available language
    const englishContent = template.content?.find((c) => c.lang === "en");
    const content =
      englishContent?.content || template.content?.[0]?.content || "";
    setMessageText(content);
    setSelectedTemplate(template.id);

    // Store all template content for translations
    setSelectedTemplateContent(template.content || []);
  };

  const handleClearTemplate = () => {
    setMessageText("");
    setSelectedTemplate(null);
    setSelectedTemplateContent([]);
  };

  const handleSaveName = async (firstName: string, lastName: string) => {
    if (currentConversation && !currentConversation.time_closed) {
      // Have active conversation, update it directly
      await handleUpdateName({
        first_name: firstName,
        last_name: lastName,
      });
    } else {
      // No active conversation, store in pending data for next new conversation
      setPendingConversationData((prev) => ({
        ...prev,
        first_name: firstName,
        last_name: lastName,
      }));

      // Also persist to server for any existing closed conversations
      if (conversations.length > 0) {
        const updatePromises = conversations
          .filter((c) => c.sms_phone_number === phoneNumber)
          .map((conv) =>
            patchConversation({
              path: { conversation_id: conv.id },
              body: { first_name: firstName, last_name: lastName },
            }),
          );
        try {
          const responses = await Promise.all(updatePromises);
          setConversations((prev) => {
            const updatedMap = new Map(
              responses
                .map((r) => r.data && [r.data.id, r.data])
                .filter(Boolean) as [string, Conversation][],
            );
            return prev.map((c) => updatedMap.get(c.id) || c);
          });
        } catch (err: any) {
          console.error("Error updating name:", err);
          const errorMessage = extractErrorMessage(
            err,
            t("chat.errors.failedUpdateName"),
          );
          toast.error(
            t("chat.errors.couldNotUpdateName", { message: errorMessage }),
          );
        }
      }
    }
  };

  const executeSend = async (
    messages: Array<{ content: string; lang: string }>,
  ) => {
    // Store message text in case we need to restore it after error
    const originalMessage = messageText;
    const originalTemplate = selectedTemplate;
    const originalTemplateContent = [...selectedTemplateContent];

    try {
      setSending(true);

      // Ensure we have an active conversation before sending
      let targetConversation = currentConversation;

      // Check if we have an open conversation, or need to create a new one
      if (targetConversation && !targetConversation.time_closed) {
        // Verify the conversation is still open by fetching latest state
        const latestConversationResponse = await getConversation({
          path: { conversation_id: targetConversation.id },
        });

        if (
          latestConversationResponse.data &&
          !latestConversationResponse.data.time_closed
        ) {
          // Conversation is still open, update with latest data
          const updatedConversation = latestConversationResponse.data;
          targetConversation = updatedConversation;
          setConversations((prev) =>
            prev.map((c) =>
              c.id === updatedConversation.id ? updatedConversation : c,
            ),
          );
          setCurrentConversation(updatedConversation);
        } else {
          // Conversation is now closed, need to create a new one
          targetConversation = null;
        }
      }

      // Create a new conversation if needed
      if (!targetConversation) {
        if (!orgId || !phoneNumber) {
          throw new Error(t("chat.errors.missingOrgOrPhone"));
        }

        const newConvResponse = await postConversation({
          body: {
            sms_phone_number: phoneNumber,
            rubi_org_id: orgId,
            auto_reply_enabled:
              pendingConversationData.auto_reply_enabled ?? true,
            first_name: pendingConversationData.first_name,
            last_name: pendingConversationData.last_name,
          },
        });

        if (!newConvResponse.data) {
          throw new Error(t("chat.errors.failedCreateConversation"));
        }

        targetConversation = newConvResponse.data;
        setConversations((prev) => [...prev, targetConversation!]);
        setCurrentConversation(targetConversation);
        // Clear pending data as it's now applied
        setPendingConversationData({});
      }

      // If a template is selected, send using template_id
      if (selectedTemplate) {
        let lastResponse;

        // Send English version first
        const enResponse = await postConversationOutboundMessage({
          path: {
            conversation_id: targetConversation.id,
          },
          body: {
            content: null,
            lang: "en",
            conversation_template_id: selectedTemplate,
          },
        });
        lastResponse = enResponse;

        // If translation is selected (messages.length > 1), send translated version
        if (messages.length > 1) {
          const translatedMessage = messages[1]; // Second message is the translation
          const translatedResponse = await postConversationOutboundMessage({
            path: {
              conversation_id: targetConversation.id,
            },
            body: {
              content: null,
              lang: translatedMessage.lang,
              conversation_template_id: selectedTemplate,
            },
          });
          lastResponse = translatedResponse;
        }

        // Update the conversation with the last response
        if (lastResponse?.data) {
          const updatedConversation = lastResponse.data;
          setConversations((prev) =>
            prev.map((c) =>
              c.id === updatedConversation.id ? updatedConversation : c,
            ),
          );
          // Ensure currentConversation is updated with the latest data
          setCurrentConversation(updatedConversation);
          targetConversation = updatedConversation;
        }
      } else {
        // Normal SMS mode: Send all messages sequentially
        let lastResponse;
        for (const message of messages) {
          const response = await postConversationOutboundMessage({
            path: {
              conversation_id: targetConversation.id,
            },
            body: {
              content: message.content,
              lang: message.lang,
              conversation_template_id: null,
            },
          });
          lastResponse = response;
        }

        // Update the conversation with the last response
        if (lastResponse?.data) {
          const updatedConversation = lastResponse.data;
          setConversations((prev) =>
            prev.map((c) =>
              c.id === updatedConversation.id ? updatedConversation : c,
            ),
          );
          // Ensure currentConversation is updated with the latest data
          setCurrentConversation(updatedConversation);
        }
      }

      setMessageText("");
      setSelectedTemplate(null);
      setSelectedTemplateContent([]);
      scrollToBottom();
    } catch (err: any) {
      console.error("Error sending message:", err);
      const errorMessage = extractErrorMessage(
        err,
        t("chat.errors.failedSendMessage"),
      );
      toast.error(errorMessage);

      // Restore the message and template state
      setMessageText(originalMessage);
      setSelectedTemplate(originalTemplate);
      setSelectedTemplateContent(originalTemplateContent);
    } finally {
      setSending(false);
    }
  };

  const handleSendMessage = async (
    messages: Array<{ content: string; lang: string }>,
  ) => {
    if (sending) return;

    // When using a template, we only need to send once with the template_id
    // Otherwise, validate that we have message text
    if (!selectedTemplate && !messageText.trim()) return;

    // Custom-message URL language check: scan non-English messages for un-tagged
    // Rubicon report URLs and prompt before sending. Template path is fully
    // handled by the backend, so skip the check there.
    if (!selectedTemplate) {
      const annotated: PendingMessage[] = messages.map((m) => ({
        message: m,
        // Skip the dialog when the message language has no services-frontend
        // equivalent: rewriting the URL would either be a no-op (Tamil) or
        // misleading (claiming "send in X" when the report page can't render X).
        candidateUrls:
          m.lang === "en" || normalizeLangForReferral(m.lang) === null
            ? []
            : findRubiconReportUrls(m.content),
      }));
      const hasCandidates = annotated.some((a) => a.candidateUrls.length > 0);
      if (hasCandidates) {
        setPendingMessages(annotated);
        return;
      }
    }

    await executeSend(messages);
  };

  const handleConfirmRewriteAndSend = async () => {
    if (!pendingMessages) return;
    const rewritten = pendingMessages.map(({ message, candidateUrls }) => {
      let content = message.content;
      candidateUrls.forEach(({ match, url }) => {
        // Replace by `match` (what the user actually typed, possibly with
        // trailing punctuation) so the rewrite lands; build the new URL from
        // the normalized `url` so query-param construction is well-formed.
        const newUrl = appendLangParam(url, message.lang);
        content = content.split(match).join(newUrl);
      });
      return { content, lang: message.lang };
    });
    setPendingMessages(null);
    await executeSend(rewritten);
  };

  const handleSendAsIs = async () => {
    if (!pendingMessages) return;
    const original = pendingMessages.map((p) => p.message);
    setPendingMessages(null);
    await executeSend(original);
  };

  const handleDismissDialog = () => {
    setPendingMessages(null);
  };

  const handleBack = () => {
    navigate(`/rubimessage/${orgId}`);
  };

  const handleStartChat = (newPhoneNumber: string) => {
    navigate(`/rubimessage/${orgId}/conversation/${newPhoneNumber}`);
  };

  const handleUpdateName = async (updates: {
    first_name?: string | null;
    last_name?: string | null;
  }) => {
    if (!currentConversation) return;

    try {
      // Update all conversations with the same phone number
      const conversationsToUpdate = conversations.filter(
        (c) => c.sms_phone_number === currentConversation.sms_phone_number,
      );

      // Send patch requests for all conversations with this phone number
      const updatePromises = conversationsToUpdate.map((conv) =>
        patchConversation({
          path: {
            conversation_id: conv.id,
          },
          body: updates,
        }),
      );

      const responses = await Promise.all(updatePromises);

      // Update state with all the responses
      setConversations((prev) => {
        const updatedMap = new Map(
          responses
            .map((r) => r.data && [r.data.id, r.data])
            .filter(Boolean) as [string, Conversation][],
        );
        return prev.map((c) => updatedMap.get(c.id) || c);
      });
    } catch (err: any) {
      console.error("Error updating name:", err);
      const errorMessage = extractErrorMessage(
        err,
        t("chat.errors.failedUpdateName"),
      );
      toast.error(
        t("chat.errors.couldNotUpdateName", { message: errorMessage }),
      );
    }
  };

  const handleUpdateConversation = async (updates: {
    sms_phone_number?: string | null;
    auto_reply_enabled?: boolean | null;
  }) => {
    if (!currentConversation) return;

    try {
      const response = await patchConversation({
        path: {
          conversation_id: currentConversation.id,
        },
        body: updates,
      });
      if (response.data) {
        setConversations((prev) =>
          prev.map((c) => (c.id === response.data!.id ? response.data! : c)),
        );
      }
    } catch (err: any) {
      console.error("Error updating conversation:", err);
      const errorMessage = extractErrorMessage(
        err,
        t("chat.errors.failedUpdateConversation"),
      );
      toast.error(
        t("chat.errors.couldNotUpdateConversation", {
          message: errorMessage,
        }),
      );
    }
  };

  // Show loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 64px)",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show error state only when there's an actual error
  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 64px)",
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", height: "calc(100vh - 64px)" }}>
      {/* Main Chat Area */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {!phoneNumber ? (
          <PhoneInputCard onSubmit={handleStartChat} />
        ) : (
          <>
            <PageHeader
              onBack={handleBack}
              left={
                <ContactNameEditor
                  firstName={
                    currentConversation?.first_name ??
                    pendingConversationData.first_name
                  }
                  lastName={
                    currentConversation?.last_name ??
                    pendingConversationData.last_name
                  }
                  phoneNumber={phoneNumber || ""}
                  onSave={handleSaveName}
                  formatPhoneNumber={formatPhoneNumber}
                />
              }
              right={
                <ToggleButtonGroup
                  value={
                    (currentConversation?.auto_reply_enabled ??
                    pendingConversationData.auto_reply_enabled ??
                    true)
                      ? "oneway"
                      : "twoway"
                  }
                  exclusive
                  onChange={(_, newValue) => {
                    if (newValue !== null) {
                      const autoReplyEnabled = newValue === "oneway";

                      if (
                        currentConversation &&
                        !currentConversation.time_closed
                      ) {
                        // Optimistic update
                        setConversations((prev) =>
                          prev.map((c) =>
                            c.id === currentConversation?.id
                              ? { ...c, auto_reply_enabled: autoReplyEnabled }
                              : c,
                          ),
                        );
                        handleUpdateConversation({
                          auto_reply_enabled: autoReplyEnabled,
                        });
                      } else {
                        setPendingConversationData((prev) => ({
                          ...prev,
                          auto_reply_enabled: autoReplyEnabled,
                        }));
                      }
                    }
                  }}
                  size="small"
                  sx={{ borderRadius: 0 }}
                >
                  <Tooltip title={t("chat.tooltips.twoWayDisabled")} arrow>
                    <span>
                      <ToggleButton
                        value="twoway"
                        color="primary"
                        disabled
                        sx={{
                          borderRadius: 0,
                          "&.Mui-selected": {
                            backgroundColor: "action.selected",
                            "&:hover": {
                              backgroundColor: "action.selected",
                            },
                          },
                        }}
                      >
                        <SwapVertIcon />
                      </ToggleButton>
                    </span>
                  </Tooltip>
                  <ToggleButton
                    value="oneway"
                    color="warning"
                    sx={{
                      borderRadius: 0,
                      "&.Mui-selected": {
                        backgroundColor: "action.selected",
                        "&:hover": {
                          backgroundColor: "action.selected",
                        },
                      },
                    }}
                  >
                    <Tooltip title={t("chat.tooltips.oneWayAutoReplyOn")} arrow>
                      <MobiledataOffIcon />
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
              }
            />

            {/* Messages Area */}
            <Box
              ref={messagesAreaRef}
              id="messages-area"
              onScroll={handleMessagesScroll}
              sx={{
                flex: 1,
                overflowY: "auto",
                p: 3,
              }}
            >
              {conversationsLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
                  <CircularProgress />
                </Box>
              )}
              {isFetchingNextPage && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    pb: 2,
                  }}
                >
                  <CircularProgress size={20} />
                </Box>
              )}
              {!isFetchingNextPage &&
                !hasNextPage &&
                conversations.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      color: "text.secondary",
                      pb: 2,
                      fontSize: 14,
                    }}
                  >
                    {t("chat.earliestConversations")}
                  </Box>
                )}
              {conversations
                .sort(
                  (a, b) =>
                    new Date(a.time_created).getTime() -
                    new Date(b.time_created).getTime(),
                )
                .map((conversation) => (
                  <ChatConversation
                    key={conversation.id}
                    conversation={conversation}
                    onConversationClosed={
                      conversation.id === currentConversation?.id
                        ? scrollToBottom
                        : undefined
                    }
                  />
                ))}
            </Box>
          </>
        )}

        {/* Message Input Area */}
        <ChatMessageBox
          supportedLanguages={Array.from(
            templates.reduce((set, t) => {
              t.content?.forEach((c) => {
                const lang = c.lang;
                if (lang && lang !== "en") {
                  set.add(lang);
                }
              });
              return set;
            }, new Set<string>()),
          )}
          messageText={messageText}
          setMessageText={setMessageText}
          onSendMessage={handleSendMessage}
          sending={sending}
          autoReplyEnabled={
            currentConversation?.auto_reply_enabled ??
            pendingConversationData.auto_reply_enabled ??
            true
          }
          autoReplyMessage={autoReplyMessage}
          oneWayNotificationMessage={oneWayNotificationMessage}
          selectedTemplateContent={selectedTemplateContent}
          onClearTemplate={handleClearTemplate}
          isManagedOnlineReporting={
            selectedTemplateObj?.is_managed_online_reporting || false
          }
          isManagedRegistry={selectedTemplateObj?.is_managed_registry || false}
          canSendFreeformMessage={canSendFreeformMessage}
          disabled={!phoneNumber}
        />
      </Box>

      {/* Templates Sidebar */}
      <TemplateSelector
        templates={templates}
        selectedTemplate={selectedTemplate}
        onTemplateSelect={handleTemplateSelect}
      />

      <LanguageMismatchDialog
        open={pendingMessages !== null}
        urls={
          pendingMessages
            ? Array.from(
                new Set(
                  pendingMessages.flatMap((p) =>
                    p.candidateUrls.map((c) => c.url),
                  ),
                ),
              )
            : []
        }
        lang={
          pendingMessages?.find((p) => p.candidateUrls.length > 0)?.message
            .lang ?? "en"
        }
        onConfirmRewrite={handleConfirmRewriteAndSend}
        onSendAsIs={handleSendAsIs}
        onDismiss={handleDismissDialog}
      />
    </Box>
  );
};

export default ChatView;
